<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("provider.title") }}</h1>
        <p class="page-head__lede">{{ t("provider.lede") }}</p>
      </div>
      <span v-if="developer" :class="['badge', statusBadge]">{{ statusLabel }}</span>
    </div>

    <div v-if="error" class="notice notice--danger" style="margin-bottom: var(--space-4)">
      <div class="notice__body">{{ error }}</div>
    </div>

    <div v-if="loading" class="card">
      <div class="row"><span class="spinner" aria-hidden="true"></span>
        <span class="muted small">{{ t("common.loading") }}</span></div>
    </div>

    <template v-else-if="developer">
      <div class="card">
        <div class="card__head">
          <div>
            <h2 class="card__title">{{ t("provider.profile") }}</h2>
            <p class="card__sub">{{ t("auth.providerNameHint") }}</p>
          </div>
        </div>

        <div class="grid grid--2">
          <div class="field">
            <label class="field__label" for="p-name">{{ t("provider.name") }}</label>
            <input id="p-name" v-model="form.name" class="input" maxlength="160" />
          </div>
          <div class="field">
            <label class="field__label" for="p-legal">{{ t("provider.legalName") }}</label>
            <input id="p-legal" v-model="form.legal_name" class="input" maxlength="240" />
          </div>
          <div class="field">
            <label class="field__label" for="p-web">{{ t("provider.website") }}</label>
            <input id="p-web" v-model="form.website" class="input" maxlength="500" placeholder="https://" />
          </div>
          <div class="field">
            <label class="field__label" for="p-country">{{ t("provider.country") }}</label>
            <input id="p-country" v-model.trim="form.country" class="input" maxlength="2" placeholder="IN" />
            <span class="field__hint">{{ t("provider.countryHint") }}</span>
          </div>
          <div class="field">
            <label class="field__label" for="p-support">{{ t("provider.supportEmail") }}</label>
            <input id="p-support" v-model="form.support_email" class="input" type="email" />
            <span class="field__hint">{{ t("auth.supportEmailHint") }}</span>
          </div>
        </div>

        <div class="row" style="margin-top: var(--space-4)">
          <button class="btn" :disabled="busy" @click="save">
            <span v-if="busy" class="spinner" aria-hidden="true"></span>
            {{ busy ? t("common.saving") : t("common.save") }}
          </button>
          <span v-if="saved" class="badge badge--success">{{ t("common.saved") }}</span>
        </div>
      </div>

      <div v-if="developer.status === 'pending' || developer.status === 'invited'" class="notice notice--info" style="margin-top: var(--space-4)">
        <div class="notice__body">
          <p class="strong">{{ t("provider.pending") }}</p>
          <p class="small">{{ t("provider.verificationPending") }}</p>
        </div>
      </div>
      <div v-else-if="developer.status === 'blocked'" class="notice notice--danger" style="margin-top: var(--space-4)">
        <div class="notice__body">{{ t("banner.providerBlocked") }}</div>
      </div>

      <p class="small muted" style="margin-top: var(--space-4)">
        {{ t("provider.teamMoved") }} <router-link to="/team">{{ t("nav.team") }}</router-link>
      </p>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { refreshMe } from "../lib/session";
import { useErrorText } from "../lib/errors";

const { t } = useI18n();
const errorText = useErrorText();

const developer = ref(null);
const loading = ref(true);
const busy = ref(false);
const saved = ref(false);
const error = ref(null);
const form = reactive({ name: "", legal_name: "", website: "", support_email: "", country: "" });

const STATUS = {
  verified: ["provider.verified", "badge--success"],
  pending: ["provider.pending", "badge--warn"],
  invited: ["provider.pending", "badge--warn"],
  blocked: ["provider.blocked", "badge--danger"],
  suspended: ["provider.suspended", "badge--danger"]
};
const statusLabel = computed(() => {
  const entry = STATUS[developer.value?.status];
  return entry ? t(entry[0]) : developer.value?.status || "";
});
const statusBadge = computed(() => (STATUS[developer.value?.status] || [])[1] || "");

async function load() {
  loading.value = true;
  try {
    const data = await api.provider();
    developer.value = data?.developer || data;
    Object.assign(form, {
      name: developer.value?.name || "",
      legal_name: developer.value?.legal_name || "",
      website: developer.value?.website || "",
      support_email: developer.value?.support_email || "",
      country: developer.value?.country || ""
    });
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}

async function save() {
  busy.value = true;
  error.value = null;
  saved.value = false;
  try {
    await api.updateProvider({ ...form, country: form.country ? form.country.toUpperCase() : null });
    await Promise.all([load(), refreshMe()]);
    saved.value = true;
    setTimeout(() => (saved.value = false), 2500);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}

onMounted(load);
</script>
